// Confirmation dialog for anything destructive (delete, reset), instead of the
// browser's own confirm(). Built on <dialog> + showModal(), so focus is kept
// inside it, Esc cancels and the page behind is inert.
import { useEffect, useRef, type ReactNode } from "react";
import { AlertTriangle } from "lucide-react";

export function ConfirmDialog({
  open,
  title,
  children,
  confirmLabel,
  busy = false,
  danger = true,
  onConfirm,
  onCancel,
}: {
  open: boolean;
  title: string;
  children: ReactNode;
  confirmLabel: string;
  busy?: boolean;
  danger?: boolean;
  onConfirm: () => void;
  onCancel: () => void;
}) {
  const ref = useRef<HTMLDialogElement>(null);

  // Open/close the native dialog with the prop
  useEffect(() => {
    const d = ref.current;
    if (!d) return;
    if (open && !d.open) d.showModal();
    if (!open && d.open) d.close();
  }, [open]);

  return (
    <dialog
      ref={ref}
      className="confirm"
      aria-labelledby="confirm-title"
      // Esc (the dialog's cancel event) or a click on the backdrop cancels
      onCancel={(e) => {
        e.preventDefault();
        if (!busy) onCancel();
      }}
      onClick={(e) => {
        if (e.target === ref.current && !busy) onCancel();
      }}
    >
      <div className="confirm-body">
        <span className={`confirm-icon${danger ? " is-danger" : ""}`}>
          <AlertTriangle size={20} aria-hidden="true" />
        </span>
        <div>
          <h2 id="confirm-title">{title}</h2>
          <div className="confirm-text">{children}</div>
        </div>
      </div>
      <div className="confirm-actions">
        <button type="button" className="btn btn-secondary" onClick={onCancel} disabled={busy} autoFocus>
          Cancel
        </button>
        <button type="button" className={`btn ${danger ? "btn-danger" : "btn-primary"}`} onClick={onConfirm} disabled={busy}>
          {busy ? "Working…" : confirmLabel}
        </button>
      </div>
    </dialog>
  );
}
